/**
 * Save files to storage, then add file urls to corresponding product
 * @param {*} firebase Firebase object containing storage functions
 * @param {*} imagesPath Storage path where images have to be stored
 * @param {*} fileArray Array containing the files to upload
 * @param {*} imageUrlArray Array of urls that get linked to product
 * @param {*} documentId ID of the product that needs to be updated
 * @param {*} firestorePath Firestore path for the document
 * @returns
 */
import { FIRESTORE_PRODUCTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../constants/CompanyConstants";
import { arrayRemove, doc, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../configs/FirebaseConfig";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import dayjs from "dayjs";

export const saveFilesToStorage = async (imagesPath, fileArray, imageUrlArray, documentId, userEmail) => {
  try {
    const safeFileArray = Array.isArray(fileArray) ? fileArray.filter(Boolean) : [];
    if (!Array.isArray(fileArray) || safeFileArray.length !== fileArray.length) {
      console.warn("[saveFilesToStorage] fileArray contained invalid entries", {
        imagesPath,
        documentId,
        originalLength: Array.isArray(fileArray) ? fileArray.length : null,
        filteredLength: safeFileArray.length,
        entries: Array.isArray(fileArray)
          ? fileArray.map((f, idx) => ({
              idx,
              type: typeof f,
              isNull: f === null,
              isUndefined: typeof f === "undefined",
              name: f?.name,
            }))
          : fileArray,
      });
    }

    const uploadPromises = safeFileArray.map(async (file, idx) => {
      if (!file?.name) {
        console.warn("[saveFilesToStorage] Skipping file without name", {
          imagesPath,
          documentId,
          idx,
          file,
        });
        return null;
      }
      // Generate unique filename
      const fileName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `${imagesPath}/${fileName}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(snapshot.ref);

      // Add to imageUrlArray
      imageUrlArray.push(downloadUrl);

      return downloadUrl;
    });

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    // Update Firestore document with new image URLs
    const productDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_TABLE,
      documentId,
    );
    await updateDoc(productDocRef, {
      productImage512DownloadURL: imageUrlArray,
      updatedAt: dayjs().unix(),
      updatedBy: userEmail,
    });

    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Delete files from storage, then remove file urls from corresponding product
 * @param {*} firebase Firebase object containing storage functions
 * @param {*} fileArray Array containing the storage paths of the files to delete
 * @param {*} productId ID of the product that needs to be updated
 * @param {*} urlArray Array containing urls that have to be removed from product
 */
export const deleteFilesFromStorage = async (fileArray, productId, urlArray, userEmail) => {
  try {
    // Delete files from storage
    const deletePromises = fileArray.map(async (filePath) => {
      const fileRef = ref(storage, filePath);
      return deleteObject(fileRef).catch((error) => console.error(error));
    });

    await Promise.all(deletePromises);

    // Remove urls from Firestore document
    const productDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_TABLE,
      productId,
    );
    await updateDoc(productDocRef, {
      productImage512DownloadURL: arrayRemove(...urlArray),
      updatedAt: dayjs().unix(),
      updatedBy: userEmail,
    });
  } catch (error) {
    console.error("Error deleting files:", error);
  }
};

/**
 * USED WHEN DELETING ENTIRE PRODUCTS
 * Delete all associated files from storage when deleting a product
 * @param {*} firebase Firebase object containing storage functions
 * @param {*} images Array of images (storage urls) that have to be deleted from storage
 */
export const deleteAssociatedFilesFromStorage = (images) => {
  if (!!images) {
    images.forEach((item) => {
      let fileRef = ref(storage, item);
      deleteObject(fileRef).catch((error) => console.log(error));
    });
  }
};

/**
 * Save files to storage for complaints (no Firestore document update)
 * @param {string} storagePath Storage path where files should be stored (e.g., "complaints")
 * @param {File[]} fileArray Array containing the files to upload
 * @param {string[]} imageUrlArray Array of urls that will be populated with download URLs
 * @returns {Promise} Promise that resolves when all files are uploaded
 */
export const saveComplaintFilesToStorage = async (storagePath, fileArray, imageUrlArray) => {
  try {
    const uploadPromises = fileArray.map(async (file) => {
      // Generate unique filename
      const fileName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `${storagePath}/${fileName}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(snapshot.ref);

      // Add to imageUrlArray
      imageUrlArray.push(downloadUrl);

      return downloadUrl;
    });

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};
